import type { SurveyQuestion, SurveySettings } from '../types/survey';

export const DEFAULT_SETTINGS: SurveySettings = {
  language: 'bilingual',
  questionCount: 6,
  shuffleQuestions: true,
  shuffleAnswers: false,
  autoAdvance: true,
  feedbackDuration: 6,
  restartDuration: 12,
};

export function shuffle<T>(items: readonly T[]): T[] {
  const result = [...items];

  for (let index = result.length - 1; index > 0; index -= 1) {
    const randomIndex = Math.floor(Math.random() * (index + 1));
    const current = result[index];
    const random = result[randomIndex];

    if (current !== undefined && random !== undefined) {
      result[index] = random;
      result[randomIndex] = current;
    }
  }

  return result;
}

export function prepareQuestions(
  questions: readonly SurveyQuestion[],
  settings: SurveySettings,
): SurveyQuestion[] {
  let pool = questions.map((question) => ({
    ...question,
    question: { ...question.question },
    explanation: { ...question.explanation },
    tip: { ...question.tip },
    answers: question.answers.map((answer) => ({
      ...answer,
      text: { ...answer.text },
    })),
  }));

  if (settings.shuffleQuestions) {
    pool = shuffle(pool);
  }

  pool = pool.slice(0, Math.min(settings.questionCount, pool.length));

  if (settings.shuffleAnswers) {
    pool = pool.map((question) => ({
      ...question,
      answers: shuffle(question.answers),
    }));
  }

  return pool;
}

export function clampSettings(
  settings: SurveySettings,
  questionLimit: number,
): SurveySettings {
  return {
    ...settings,
    questionCount: Math.max(1, Math.min(settings.questionCount, questionLimit)),
    feedbackDuration: Math.max(3, Math.min(settings.feedbackDuration, 15)),
    restartDuration: Math.max(5, Math.min(settings.restartDuration, 30)),
  };
}

export function getResultMessage(percent: number) {
  if (percent >= 80) {
    return {
      fr: 'Excellent ! Vous maîtrisez les bonnes pratiques.',
      ar: 'ممتاز! أنت تتقن الممارسات الصحيحة.',
    };
  }

  if (percent >= 50) {
    return {
      fr: 'Bien joué ! Quelques réflexes peuvent encore être améliorés.',
      ar: 'أحسنت! ما زالت بعض الممارسات بحاجة إلى تحسين.',
    };
  }

  return {
    fr: 'Merci d’avoir participé. Retenez ces conseils essentiels.',
    ar: 'شكراً لمشاركتك. تذكّر هذه النصائح الأساسية.',
  };
}
